import { useState } from "react";
import { Table, Button } from "react-bootstrap";

import { MultiMpanTableProps } from "../../types/components.types";
import {
  downloadMultiMpanCSV,
  downloadHHCalculationsExport,
} from "../../utils/electralink/electralink.csv";
import {
  flattenMeterDetailsForTable,
  flattenTaqOnlyForTable,
  hasHalfHourlyMeters,
  getHalfHourlyResults,
} from "../../utils/electralink/electralink.utils";

const MultiMpanTable: React.FC<MultiMpanTableProps> = ({ results }) => {
  // Track whether the HH export is currently running, to prevent duplicate clicks on the export button.
  const [isExporting, setIsExporting] = useState(false);

  // Track the total number of MPANs returned to determine the layout logic rendered on the UI.
  const mpanCount = results.length;

  // Check if any MPAN is half-hourly as that impacts the UI logic.
  const hasHH = hasHalfHourlyMeters(results);

  // Handle the "Download HH Calculations" button trigger.
  const handleHHExport = async () => {
    // Set the loading state to true to disable the button whilst the download is taking place.
    setIsExporting(true);
    try {
      // Call the function that handles the CSV/Zip generation.
      const result = await downloadHHCalculationsExport(results);
      if (!result.success) {
        alert(`Failed to export HH calculations: ${result.error ?? "Unknown error"}`);
      }
    } catch (error) {
      console.log("Error exporting HH calculations:", error);
      alert("An error occurred while exporting HH calculations. Please try again.");
    } finally {
      // Reset the loading state.
      setIsExporting(false);
    }
  };

  // For 5+ MPANs with HH meters: only show TAQ calculations in the table.
  // For 5+ MPANs without HH: no table, just the download button.
  // For 2-5 MPANs: show full table (with TAQ row if HH meters exist).
  const isFivePlusMpans = mpanCount > 5;

  // 5+ MPANs with HH --> Display the TAQ only for the relevant MPANs.
  // Otherwise --> display the entire data in a table.
  const tableData =
    isFivePlusMpans && hasHH
      ? flattenTaqOnlyForTable(results)
      : flattenMeterDetailsForTable(results);

  // Filter out rows where all values are empty or have errored. Keep only the rows with valid data.
  const filteredRows = tableData.rows.filter((row) =>
    row.values.some((value) => value.display !== "" && !value.isError),
  );

  // For 5+ MPANs with no HH meters: only show the "download CSV" button (no table).
  if (isFivePlusMpans && !hasHH) {
    return (
      <div className="multi-mpan-results">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Search Results ({results.length} MPANs)</h5>
          <Button
            variant="primary"
            data-testid="electralink-multi-mpan-download-csv"
            onClick={() => {
              downloadMultiMpanCSV(results);
            }}
          >
            Download CSV
          </Button>
        </div>
      </div>
    );
  }

  // Count the successful HH meters to determine whether the export is CSV or a ZIP file.
  const hhCount = getHalfHourlyResults(results).length;

  return (
    <div className="multi-mpan-results">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>
          {isFivePlusMpans && hasHH
            ? `Half-Hourly Meter Data (${String(tableData.columns.length)} HH MPANs)`
            : `Search Results (${String(results.length)} MPANs)`}
        </h5>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            data-testid="electralink-multi-mpan-download-csv"
            onClick={() => {
              downloadMultiMpanCSV(results);
            }}
          >
            Download CSV
          </Button>
          {/* Conditionally render HH export button if there are HH meters in the results. */}
          {hasHH && (
            <Button
              variant="secondary"
              data-testid="electralink-multi-mpan-download-hh"
              onClick={() => {
                void handleHHExport();
              }}
              disabled={isExporting}
            >
              {isExporting
                ? "Exporting..."
                : `Download HH Calculations${hhCount > 1 ? " (ZIP)" : ""}`}
            </Button>
          )}
        </div>
      </div>

      <div className="multi-mpan-table-container">
        <Table striped bordered hover data-testid="electralink-multi-mpan-table">
          <thead>
            <tr>
              {/* Freeze the left column, so it stays visible when scrolling. */}
              <th className="sticky-field-column" rowSpan={2}>
                Field
              </th>
              <th colSpan={tableData.columns.length} className="text-center">
                MPAN
              </th>
            </tr>
            <tr>
              {tableData.columns.map((column) => (
                <th
                  key={column.mpan}
                  className={column.hasError ? "error-column" : ""}
                  data-testid={`electralink-multi-mpan-column-${column.mpan}`}
                >
                  {column.mpan}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* One row per data field. */}
            {filteredRows.map((row) => (
              <tr key={row.field}>
                <td className="sticky-field-column">{row.field}</td>
                {row.values.map((value, index) => (
                  <td key={index} className={value.isError ? "text-danger" : ""}>
                    {value.display}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default MultiMpanTable;
